import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from 'app';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { Auth0Client } from '@auth0/auth0-spa-js';
import detectHeadlessBrowser from 'helpers/detectHeadlessBrowser';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
import {
  processSessionPinFromQuery,
  getCookie,
  redirectToMFE,
} from './mfeRouting';

const isHeadlessBrowser = detectHeadlessBrowser();
const headlessBrowserSharedContextKey = 'headless-browser';

/**
 * Check if user should be routed to MFE version
 * Returns: true = stay on legacy, false = redirect to MFE
 *
 * Routing Logic:
 * 1. Session pin (?mfe=on/off) takes precedence over all flags
 * 2. mfeEnabled flag (default: false) - controls cutover (false = legacy, true = MFE)
 * 3. Dev bypass: users with 'dev' role always get MFE regardless of flags
 */
async function checkMfeRouting(): Promise<boolean> {
  try {
    const url = new URL(window.location.href);

    // Skip MFE routing during Auth0 callback - let Auth0 complete its flow first
    if (url.searchParams.has('code') || url.searchParams.has('state')) {
      console.log(
        '[Legacy] MFE routing: Auth0 callback detected, skipping MFE routing'
      );
      return true; // Stay on legacy to complete Auth0 callback
    }

    // Process ?mfe=on/off query parameter
    processSessionPinFromQuery();

    // Check session pin override - these take precedence
    const sessionPin = getCookie('mfe_session_pin');

    if (sessionPin === 'off') {
      // Force legacy - stay here
      console.log('[Legacy] MFE routing: Session pin = off, staying on legacy');
      return true;
    }

    if (sessionPin === 'on') {
      // Force MFE - redirect
      console.log('[Legacy] MFE routing: Session pin = on, redirecting to MFE');
      redirectToMFE();
      return false;
    }

    // Try to get authenticated user from Auth0
    const auth0 = new Auth0Client({
      domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
      clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_CLIENT_AUDIENCE || '',
      },
      cacheLocation: 'localstorage',
    });

    const isAuthenticated = await auth0.isAuthenticated();

    if (!isAuthenticated) {
      // Not authenticated locally - try silent auth via Auth0 SSO
      try {
        console.log(
          '[Legacy] MFE routing: Not authenticated, attempting silent auth via SSO'
        );
        await auth0.getTokenSilently();
        console.log('[Legacy] MFE routing: Silent auth successful via SSO');
        // Token retrieved silently - continue with MFE routing check
      } catch (silentAuthError) {
        console.log(
          '[Legacy] MFE routing: Silent auth failed, staying on legacy:',
          silentAuthError
        );
        return true;
      }
    }

    // Get user info for LaunchDarkly context
    const user = await auth0.getUser();
    if (!user) {
      console.log('[Legacy] MFE routing: No user info, staying on legacy');
      return true;
    }

    const userId = user.sub || 'anonymous';
    const orgId = (user as any).org_id || (user as any).organizationId || '';
    const roles = (user as any).roles || [];

    // Dev bypass - devs always get MFE
    const isDev = roles.includes('dev');

    // Initialize LaunchDarkly with user context to check flags
    const ldClientSideId = process.env.REACT_APP_LD_CLIENT_SIDE_ID;
    if (!ldClientSideId) {
      console.warn(
        '[Legacy] MFE routing: LD client ID not configured, staying on legacy'
      );
      return true; // Stay on legacy if LD not configured
    }

    const ldContext: LDClient.LDContext = {
      kind: 'user',
      key: userId,
      orgId,
      roles,
      // Also add as custom attributes for better LD compatibility
      custom: {
        orgId,
        roles: roles.join(','),
      },
    };

    console.log('[Legacy] MFE routing: Initializing LD with context', {
      userId,
      orgId,
      roles,
      isDev,
    });

    const ldClient = LDClient.initialize(ldClientSideId, ldContext);
    await ldClient.waitForInitialization();

    // Check mfe-enabled flag (cutover control)
    // Note: Raw JS SDK uses kebab-case, React SDK converts to camelCase
    const mfeEnabled = ldClient.variation('mfe-enabled', false);

    console.log('[Legacy] MFE routing: Flag evaluation', {
      mfeEnabled,
      isDev,
      willRedirect: mfeEnabled || isDev,
    });

    // Clean up LD client (React SDK will initialize its own)
    ldClient.close();

    if (mfeEnabled || isDev) {
      // User is in MFE cohort (or is dev) - redirect to MFE
      console.log('[Legacy] MFE routing: Redirecting to MFE');
      redirectToMFE();
      return false;
    }

    // User is not in rollout cohort - stay on legacy
    console.log('[Legacy] MFE routing: Staying on legacy');
    return true;
  } catch (error) {
    // On error, default to staying on legacy (fail-safe)
    console.error(
      '[Legacy] MFE routing check failed, defaulting to legacy',
      error
    );
    return true;
  }
}

(async () => {
  // FIRST: Check for sandbox mode parameter and enable if present
  const url = new URL(window.location.href);
  const sandboxParam = url.searchParams.get('sandbox');
  if (sandboxParam === 'true') {
    localStorage.setItem('testEnv', 'true');
    url.searchParams.delete('sandbox');
    history.replaceState(null, '', url.toString());
  }

  // SECOND: Check MFE routing and potentially redirect
  const shouldStayOnLegacy = await checkMfeRouting();

  if (!shouldStayOnLegacy) {
    // User is being redirected to MFE - don't render anything
    return;
  }

  // THIRD: User should stay on legacy - continue with normal app initialization
  const initialContext = {
    kind: 'user',
    anonymous: true,
    key: isHeadlessBrowser ? headlessBrowserSharedContextKey : undefined,
  };
  const LDProvider = await asyncWithLDProvider({
    clientSideID: process.env.REACT_APP_LD_CLIENT_SIDE_ID || '',
    options: {
      streaming: true,
    },
    context: initialContext,
    reactOptions: {
      useCamelCaseFlagKeys: true,
    },
  });

  const router = createBrowserRouter(
    createRoutesFromElements(<Route path="*" element={<App />} />)
  );

  const container = document.getElementById('root');
  if (!container) throw new Error('Failed to find the root element');
  const root = createRoot(container);

  root.render(
    <LDProvider>
      <RouterProvider router={router} />
    </LDProvider>
  );
})();
